import { ArticleJsonLd, NextSeo } from 'next-seo'
import { useRouter } from 'next/router'

import { getBaseUrl } from '@/lib/contentful/utils'

import { isNonNullable } from '@/utils/isNonNullable'
import { getSeoLocale } from '@/utils/locales/getSeoLocale'
import { getLocalizedFromEnglish } from '@/utils/locales/localized-routes/getLocalizedFromEnglish'
import { useUrl } from '@/utils/url/useUrl'

import { useEntry } from '../Article.hooks'

type Props = {
	children: React.ReactNode
}

export const slugLocalized = <D extends object>(
	entry: { data: D },
	locale?: string,
	parentArticle?: D | null | undefined
) => {
	const articleSlugLocaleKey = `slug_${locale?.replace('-', '_')}` as keyof D

	if (parentArticle && parentArticle[articleSlugLocaleKey]) {
		return `${parentArticle[articleSlugLocaleKey]}/${entry.data[articleSlugLocaleKey]}`
	}

	return entry.data[articleSlugLocaleKey]
}

export function ArticleMeta({ children }: Props) {
	const entry = useEntry()
	const { locale = 'en-US', asPath } = useRouter()
	const path = asPath.split('/').filter(Boolean)
	const url = useUrl({ locale })
	const baseUrl = `${url?.protocol}//${url?.host}`

	if ('error' in entry) return <>{children}</>

	const {
		seoMetadata,
		parentArticle,
		status_en_US,
		status_en_GB,
		status_en_AU,
		status_it_IT,
		status_nl_NL,
		status_fr_FR,
		status_es_ES,
		status_de_DE,
		status_es_MX,
		author
	} = entry.data

	const title = seoMetadata?.title ?? ''
	const description = seoMetadata?.description ?? ''

	const liveStatusValue = '🟢 live'

	return (
		<>
			<NextSeo
				title={title}
				description={description}
				canonical={
					seoMetadata?.canonicalUrl ||
					`${baseUrl}${getLocalizedFromEnglish(
						locale as Locale,
						`/${path.at(0)}/${slugLocalized(entry, locale, parentArticle)}/`
					)}`
				}
				languageAlternates={[
					status_en_US === liveStatusValue
						? {
								hrefLang: 'en-us',
								href: `${getBaseUrl('en-US')}/articles/${slugLocalized(
									entry,
									'en-US',
									parentArticle
								)}/`
						  }
						: null,
					status_en_AU === liveStatusValue
						? {
								hrefLang: 'en-au',
								href: `${getBaseUrl('en-AU')}/articles/${slugLocalized(
									entry,
									'en-AU',
									parentArticle
								)}/`
						  }
						: null,
					status_en_GB === liveStatusValue
						? {
								hrefLang: 'en-gb',
								href: `${getBaseUrl('en-GB')}/articles/${slugLocalized(
									entry,
									'en-GB',
									parentArticle
								)}/`
						  }
						: null,
					status_fr_FR === liveStatusValue
						? {
								hrefLang: 'fr-fr',
								href: `${getBaseUrl('fr-FR')}/articles/${slugLocalized(
									entry,
									'fr-FR',
									parentArticle
								)}/`
						  }
						: null,
					status_it_IT === liveStatusValue
						? {
								hrefLang: 'it-it',
								href: `${getBaseUrl('it-IT')}/articoli/${slugLocalized(
									entry,
									'it-IT',
									parentArticle
								)}/`
						  }
						: null,
					status_nl_NL === liveStatusValue
						? {
								hrefLang: 'nl-nl',
								href: `${getBaseUrl('nl-NL')}/artikelen/${slugLocalized(
									entry,
									'nl-NL',
									parentArticle
								)}/`
						  }
						: null,
					status_es_ES === liveStatusValue
						? {
								hrefLang: 'es-es',
								href: `${getBaseUrl('es-ES')}/articulos/${slugLocalized(
									entry,
									'es-ES',
									parentArticle
								)}/`
						  }
						: null,
					status_es_MX === liveStatusValue
						? {
								hrefLang: 'es-mx',
								href: `${getBaseUrl('es-MX')}/articulos/${slugLocalized(
									entry,
									'es-MX',
									parentArticle
								)}/`
						  }
						: null,
					status_de_DE === liveStatusValue
						? {
								hrefLang: 'de-de',
								href: `${getBaseUrl('de-DE')}/artikel/${slugLocalized(
									entry,
									'de-DE',
									parentArticle
								)}/`
						  }
						: null,
					status_en_US === liveStatusValue
						? {
								hrefLang: 'x-default',
								href: `${getBaseUrl('en-US')}/articles/${slugLocalized(
									entry,
									'en-US',
									parentArticle
								)}/`
						  }
						: null
				].filter(isNonNullable)}
				nofollow={Boolean(seoMetadata?.nofollow)}
				noindex={Boolean(seoMetadata?.noindex)}
				openGraph={{
					title,
					description,
					url: `${baseUrl}${getLocalizedFromEnglish(
						locale as Locale,
						`/${path.at(0)}/${slugLocalized(entry, locale, parentArticle)}/`
					)}`,
					images: seoMetadata?.image?.url
						? [
								{
									url: seoMetadata?.image?.url,
									width: seoMetadata?.image?.width,
									height: seoMetadata?.image?.height,
									alt: 'Bigcommerce logo',
									type: 'image/jpeg'
								}
						  ]
						: undefined,
					locale: getSeoLocale(locale)
				}}
			/>

			<ArticleJsonLd
				type="Article"
				datePublished={new Date(entry?.data?.publishedDate ?? new Date())?.toISOString()}
				dateModified={new Date(entry?.data?.sys?.publishedAt ?? new Date())?.toISOString()}
				isAccessibleForFree={true}
				publisherName="BigCommerce"
				authorName={author?.fullName ?? 'BigCommerce Team'}
				title={title}
				description={description}
				images={[seoMetadata?.image?.url ?? '']}
				url={`${getBaseUrl(locale)}/${path.at(0)}/${slugLocalized(entry, locale, parentArticle)}/`}
			/>
			{children}
		</>
	)
}
